import type { ReportFilters, ReportGrouping } from '../types/report.types';

/**
 * The report filters' URL keys. A change is written as one patch of these
 * keys (null removes one), so brand and denomination change together.
 */
export interface ReportFilterChanges {
  channel?: string | null;
  providerId?: string | null;
  brandId?: string | null;
  denominationId?: string | null;
}

export const REPORT_FILTER_KEYS = ['channel', 'providerId', 'brandId', 'denominationId'] as const;

/** Every filter removed. */
export const CLEAR_REPORT_FILTERS: ReportFilterChanges = {
  channel: null,
  providerId: null,
  brandId: null,
  denominationId: null,
};

function positiveId(raw: string | null): number | undefined {
  if (!raw || !/^\d+$/.test(raw)) return undefined;
  const id = Number(raw);
  return id > 0 ? id : undefined;
}

/** The filters in the URL; anything malformed is ignored rather than sent. */
export function readReportFilters(searchParams: URLSearchParams): ReportFilters {
  const channel = searchParams.get('channel')?.trim();
  return {
    channel: channel ? channel : undefined,
    providerId: positiveId(searchParams.get('providerId')),
    brandId: positiveId(searchParams.get('brandId')),
    denominationId: positiveId(searchParams.get('denominationId')),
  };
}

export const hasReportFilters = (filters: ReportFilters) =>
  filters.channel !== undefined ||
  filters.providerId !== undefined ||
  filters.brandId !== undefined ||
  filters.denominationId !== undefined;

/**
 * The filter a grouped row stands for — its key is the channel code, or the
 * provider, brand or denomination id. Null for other groupings and for the
 * "Unknown" group (no channel or no id recorded), which cannot be filtered on.
 */
export function filterForGroup(grouping: ReportGrouping, key: string): ReportFilterChanges | null {
  switch (grouping) {
    case 'channel':
      return key && key.toLowerCase() !== 'unknown' ? { channel: key } : null;
    case 'provider':
      return positiveId(key) ? { providerId: key } : null;
    case 'brand':
      // A denomination belongs to one brand: changing the brand clears it.
      return positiveId(key) ? { brandId: key, denominationId: null } : null;
    case 'denomination': {
      // The key is "brandId:denominationId", so the row sets both and the
      // Denomination select (which needs a brand) shows the chosen one.
      const [brandId, denominationId] = key.split(':');
      return positiveId(brandId ?? null) && positiveId(denominationId ?? null)
        ? { brandId, denominationId }
        : null;
    }
    default:
      return null;
  }
}

/** The grouping's name in "Show only …" labels. */
export const FILTERABLE_GROUP_NOUN: Partial<Record<ReportGrouping, string>> = {
  channel: 'payment channel',
  provider: 'provider',
  brand: 'brand',
  denomination: 'denomination',
};
